import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useNavigation } from "@remix-run/react";
import { useThrottledCallback } from "use-debounce";

import {
  createPresentLinkMessage,
  presentReportIssue,
  sendMessageToHost,
} from "~/utils/events";
import { sharePage } from "~/utils/share";
import { getSlideIndex } from "~/utils/slides";
import { getArtworkFromData, getDownloadFromData } from "~/utils/data";
import { track } from "~/utils/track";
import { isMobileNative } from "~/utils/helpers";
import { logger } from "~/utils/logging";
import { pathFor, urlFor } from "~/utils/url";
import { isPipSupported, showShareButton } from "~/utils/features";
import { toApiNoteColor } from "~/utils/api";
import { useRouteAction } from "~/hooks/useRouteAction";
import { usePageState } from "~/hooks/usePageState";
import { useLabels } from "~/hooks/useLabels";
import { useSessionSwitcher } from "~/hooks/useSessionSwitcher";
import { useUserContext } from "~/hooks/useUserContext";
import { useMountEffect } from "~/hooks/useMountEffect";
import { useUnmountEffect } from "~/hooks/useUnmountEffect";
import { usePrevious } from "~/hooks/usePrevious";
import { useToasts } from "~/components/toast/useToasts";
import { MediaPlayerViewMode } from "~/components/media/types";
import { When } from "~/components/when/When";
import { Button } from "~/components/button/Button";
import { ButtonGroup } from "~/components/buttonGroup/ButtonGroup";
import { TextInput } from "~/components/textInput/TextInput";
import { SessionActionsModal } from "~/components/sessionActionsModal/SessionActionsModal";
import { ToastStack } from "~/components/toast/ToastStack";
import { WatchView } from "~/components/watchView/WatchView";
import { useWatchView } from "~/components/watchView/useWatchView";
import {
  WatchViewComputedCustomProperties,
  WatchViewMediaMode,
  WatchViewSubview,
  WatchViewViewMode,
} from "~/components/watchView/WatchViewManager";
import {
  RouteType,
  Action,
  SESSION_PROGRESS_SAVE_INTERVAL,
  UNKNOWN,
} from "~/constants";
import { ShareModal } from "~/components/shareModal/ShareModal";
import {
  clearDraftData,
  createStorageKey,
  getDraftData,
  setDraftData,
} from "~/components/watchView/helpers";
import { Tab, TabList, TabPanel, Tabs } from "~/components/tabs/Tabs";
import { ClassroomDownloadType } from "~/graphql/types";
import { SegmentedControl } from "~/components/segmentedControl/SegmentedControl";

import { SessionReviewReflect } from "./SessionReviewReflect";
import { SessionVideoPlayer } from "./SessionVideoPlayer";
import { SessionSlideLinks } from "./SessionSlideLinks";
import { SessionSlides } from "./SessionSlides";
import { SessionAudioPlayer } from "./SessionAudioPlayer";
import { SessionTeacherNotes } from "./SessionTeacherNotes";
import { SessionNotebook } from "./SessionNotebook";
import { SessionUserMenu } from "./SessionUserMenu";
import { SessionReflection } from "./SessionReflection";
import { SessionTranscript } from "./SessionTranscript";
import { SessionDownloads } from "./SessionDownloads";

import "./SessionDetail.css";

import type { SyntheticEvent } from "react";
import type { Session, Class, ClassroomImage } from "~/graphql/types";
import type { MediaPlayerFullscreenWindowPosition } from "~/components/media/types";
import type { ActionProp } from "~/components/components";
import type { ButtonProps } from "~/components/button/Button";
import type { WatchViewLayoutEvent } from "~/components/watchView/WatchView";
import type { WatchViewManagerState } from "~/components/watchView/WatchViewManager";
import type {
  NoteEditorData,
  NoteEditorModalSaveEvent,
} from "~/components/noteEditorModal/NoteEditorModal";
import type { loader } from "./route";

export function SessionDetail() {
  const { t } = useLabels();

  const data = useLoaderData<typeof loader>();

  const { currentUser, hostContext, preferences, setPreference, promptLogin } =
    useUserContext();

  const { submitAction, intent } = useRouteAction();

  const transition = useNavigation();

  const { showToast, dismissAllToasts } = useToasts();

  const { menuItems } = useSessionSwitcher(data.classItems as Partial<Class>);

  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  // Track session ID changes in order to reset the view state
  const prevSessionId = usePrevious(data.session.id);

  const showShareModal = useCallback(() => {
    setIsActionsOpen(false);
    setIsShareOpen(true);
  }, []);

  // A clock to track information about the last progress save
  const progressSaveClockRef = useRef({ isSaving: false, time: Date.now() });

  const draftNoteStorageKey = createStorageKey(
    `${data.session.class?.slug}:${data.session.position}`,
  );

  const draftData = useMemo<Partial<NoteEditorData>>(() => {
    return getDraftData(draftNoteStorageKey) ?? {};
  }, [draftNoteStorageKey]);

  // Build an initial state for view
  const initialState: Partial<WatchViewManagerState> = {
    currentTime: data.startTime,
    draftNoteColor: draftData.color ?? preferences.noteColor,
    draftNoteValue: draftData.value,
    mediaMode: preferences.audioOnly
      ? WatchViewMediaMode.Audio
      : WatchViewMediaMode.Video,
  };

  // View state logic is managed by this hook
  const view = useWatchView({
    defaultState: initialState,
  });

  /////////////////////
  // COMPUTED VALUES //
  /////////////////////

  const shareUrl = urlFor(RouteType.SESSION, {
    class: data.session.class?.slug!,
    session: String(data.session.position),
  });

  const trackingData = useMemo(
    () => ({
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      timestamp: view.state.currentTime,
      trigger: "sessionWatch",
    }),
    [data.session.class?.slug, data.session.position, view.state.currentTime],
  );

  const isCompleteOptimistic = useMemo(
    () =>
      intent === Action.MarkSessionComplete ||
      intent === Action.UnmarkSessionComplete
        ? intent === Action.MarkSessionComplete
        : Boolean(data.session.user?.completedAt),
    [data.session.user?.completedAt, intent],
  );

  const headerButtonCommonProps = useMemo<Partial<ButtonProps>>(
    () => ({
      appearance: view.isDesktopLayout ? "control" : "minimal",
      shape: "circle",
    }),
    [view.isDesktopLayout],
  );

  const canExpandSlides = useMemo(() => {
    const isViewModeCompatible =
      view.state.viewMode === WatchViewViewMode.Splitscreen;
    const isAudioOnly = view.state.mediaMode === "audio";
    const isVideoStateCompatible =
      !view.playerContextRef.current?.airplaying && isPipSupported();

    return isViewModeCompatible && (isAudioOnly || isVideoStateCompatible);
  }, [view.playerContextRef, view.state.mediaMode, view.state.viewMode]);

  const toastStackInsetBottom = useMemo(() => {
    if (view.isDesktopLayout) return;

    if (view.state.viewMode === WatchViewViewMode.Fullscreen) {
      return "calc(var(--safe-area-inset-bottom) + var(--size-2))";
    }

    return "calc(var(--size-9) + max(var(--safe-area-inset-bottom), var(--size-2)))";
  }, [view.isDesktopLayout, view.state.viewMode]);

  const actions = useMemo<ActionProp[]>(() => {
    const teacherNotesDownloadUrl = getDownloadFromData(
      (data.session.class as any).downloads,
      ClassroomDownloadType.TeacherNotes,
    )?.url;

    const literalLiteraryTranslationDownloadUrl = getDownloadFromData(
      (data.session.class as any).downloads,
      ClassroomDownloadType.LiteralLiteraryTranslation,
    )?.url;

    return [
      ...(showShareButton()
        ? [
            {
              icon: "arrow-up-from-square",
              text: t("common.cta.share"),
              onClick: (e: SyntheticEvent) => {
                if (isMobileNative()) {
                  sharePage(shareUrl, e.target);
                } else {
                  showShareModal();
                }

                track("crSessionShare", trackingData);
              },
            },
          ]
        : []),
      {
        icon: "circle-check",
        iconVariant: isCompleteOptimistic ? "solid" : "regular",
        text: isCompleteOptimistic
          ? t("common.cta.unmarkSessionComplete")
          : t("common.cta.markSessionComplete"),
        onClick() {
          submitAction(
            {
              intent: isCompleteOptimistic
                ? Action.UnmarkSessionComplete
                : Action.MarkSessionComplete,
              sessionId: data.session.id,
            },
            { authRequired: true },
          ).catch((error) => {
            logger.error(error);
          });
        },
      },
      {
        icon: "message-exclamation",
        text: t("common.cta.reportIssue"),
        onClick() {
          presentReportIssue({
            url: window.location.href,
            userAgent: window.navigator.userAgent,
            webVersion: window.__env__?.VERSION ?? UNKNOWN,
            session: data.session.position,
            timestamp: view.state.currentTime,
            slideId:
              data.session.slides?.[
                getSlideIndex(view.state.currentTime, data.session.slides)
              ].id,
          }).catch((error) => {
            logger.error(error);
          });
        },
      },
      ...(teacherNotesDownloadUrl && !view.isDesktopLayout
        ? [
            {
              icon: "circle-arrow-down",
              text: t("common.cta.downloadTeacherNotes"),
              onClick() {
                track("crDownloadTeacherNotes", {
                  className: data.session.class?.slug,
                  sessionNumber: data.session.position,
                  trigger: "sessionNotebook",
                });

                if (isMobileNative()) {
                  sendMessageToHost(
                    createPresentLinkMessage(teacherNotesDownloadUrl),
                  );
                } else {
                  globalThis.open(teacherNotesDownloadUrl);
                }
              },
            },
          ]
        : []),
      ...(literalLiteraryTranslationDownloadUrl && !view.isDesktopLayout
        ? [
            {
              icon: "circle-arrow-down",
              text: t("common.cta.downloadLiteralLiteraryTranslation"),
              onClick() {
                track("crDownloadLiteralLiteraryTranslation", {
                  className: data.session.class?.slug,
                  sessionNumber: data.session.position,
                  trigger: "sessionNotebook",
                });

                if (isMobileNative()) {
                  sendMessageToHost(
                    createPresentLinkMessage(
                      literalLiteraryTranslationDownloadUrl,
                    ),
                  );
                } else {
                  globalThis.open(literalLiteraryTranslationDownloadUrl);
                }
              },
            },
          ]
        : []),
    ];
  }, [
    t,
    isCompleteOptimistic,
    trackingData,
    shareUrl,
    showShareModal,
    submitAction,
    data.session.id,
    data.session.position,
    data.session.slides,
    data.session.class,
    view.state.currentTime,
    view.isDesktopLayout,
  ]);

  const saveProgress = useCallback(
    (timestamp: number) => {
      // Only save progress if the user is logged in
      if (!currentUser) return;

      // Only save if the timestamp is valid (not zero)
      if (!timestamp) return;

      // Only submit if all previous attempts have settled
      if (progressSaveClockRef.current.isSaving) return;

      progressSaveClockRef.current.isSaving = true;

      submitAction<Partial<Session>>({
        intent: Action.UpdateSessionProgress,
        sessionId: data.session.id,
        timestamp: Math.round(timestamp),
      })
        .then((result) => {
          // TODO move this to the server once a server-side
          // tracking solution is in place
          if (result?.user?.didJustComplete) {
            track("crSessionComplete", {
              ...trackingData,
              trigger: "sessionProgressUpdate",
            });
          }
        })
        .catch((error) => {
          logger.error(`Action ${Action.UpdateSessionProgress} failed.`, error);
        })
        .finally(() => {
          progressSaveClockRef.current.isSaving = false;
          progressSaveClockRef.current.time = Date.now();
        });
    },
    [currentUser, data.session.id, submitAction, trackingData],
  );

  //////////////
  // HANDLERS //
  //////////////

  const handleTimeUpdate = useThrottledCallback(async (time: number) => {
    // Update manager state
    view.setCurrentTime(time);

    // Save progress if the user is logged in and progress
    // is not currently being saved
    if (currentUser && !progressSaveClockRef.current.isSaving) {
      // Calculate the elapsed time since the last save
      const elapsedTime =
        (Date.now() - progressSaveClockRef.current.time) / 1_000;

      // Save progress if enough time has elapsed
      if (elapsedTime >= SESSION_PROGRESS_SAVE_INTERVAL) {
        saveProgress(time);
      }
    }
  }, 1_000);

  const handleSaveSlideToNotes = useCallback(
    async ({ content, timestamp }: { content: string; timestamp: number }) => {
      await submitAction({
        intent: Action.CreateNote,
        input: {
          sessionId: data.session.id,
          timestamp,
          content,
        },
      });
    },
    [data.session.id, submitAction],
  );

  const handleReflectionSave = useCallback(
    async (data: { id: string; value: string }) => {
      await submitAction({
        intent: Action.SaveReflectionResponse,
        input: {
          reflectionId: data.id,
          content: data.value,
        },
      });
    },
    [submitAction],
  );

  const handleNotebookToggle = useCallback(() => {
    const isNotebookOpen = view.state.subview === WatchViewSubview.Notebook;

    view.setSubview(WatchViewSubview.Notebook);

    if (!isNotebookOpen) {
      track("crViewNotebook", trackingData);
    }
  }, [trackingData, view]);

  const handleTeacherNotesToggle = useCallback(() => {
    const isTeacherNotesOpen =
      view.state.subview === WatchViewSubview.TeacherNotes;

    if (!isTeacherNotesOpen) {
      track("crViewTeacherNotes", {
        className: data.session.class?.slug,
        sessionNumber: data.session.position,
        trigger: "sessionNotebook",
      });
    }

    view.setSubview(WatchViewSubview.TeacherNotes);
  }, [data.session.class?.slug, data.session.position, view]);

  const handleTranscriptToggle = useCallback(() => {
    const isTranscriptOpen = view.state.subview === WatchViewSubview.Transcript;

    view.setSubview(WatchViewSubview.Transcript);

    if (!isTranscriptOpen) {
      track("crTranscriptView", trackingData);
    }
  }, [trackingData, view]);

  const handleReflectionToggle = useCallback(() => {
    const isReflectionOpen = view.state.subview === WatchViewSubview.Reflection;

    if (!isReflectionOpen) {
      track("crViewReflection", {
        className: data.session.class?.slug,
        sessionNumber: data.session.position,
        trigger: "sessionNotebook",
      });
    }

    view.setSubview(WatchViewSubview.Reflection);
  }, [data.session.class?.slug, data.session.position, view]);

  const handleDownloadsToggle = useCallback(() => {
    const isDownloadsOpen = view.state.subview === WatchViewSubview.Downloads;

    if (!isDownloadsOpen) {
      track("crViewDownloads", trackingData);
    }

    view.setSubview(WatchViewSubview.Downloads);
  }, [trackingData, view]);

  const handleSlidesToggle = useCallback(() => {
    const isSlidesOpen = view.state.subview === WatchViewSubview.Slides;

    if (!isSlidesOpen) {
      track("crViewSlides", trackingData);
    }

    view.setSubview(WatchViewSubview.Slides);
  }, [trackingData, view]);

  const handleDrawerToggle = useCallback(
    ({ selected }: { selected: number }) => {
      switch (selected) {
        case 1:
          handleTeacherNotesToggle();
          break;
        case 2:
          handleTranscriptToggle();
          break;
        case 3:
          handleReflectionToggle();
          break;
        case 4:
          handleDownloadsToggle();
          break;
        default:
          handleNotebookToggle();
      }
    },
    [
      handleTeacherNotesToggle,
      handleTranscriptToggle,
      handleReflectionToggle,
      handleDownloadsToggle,
      handleNotebookToggle,
    ],
  );

  const handleTabsToggle = useCallback(
    ({ selected }: { selected: number }) => {
      switch (selected) {
        case 1:
          handleNotebookToggle();
          break;
        case 2:
          handleTeacherNotesToggle();
          break;
        case 3:
          handleTranscriptToggle();
          break;
        case 4:
          handleReflectionToggle();
          break;
        default:
          handleSlidesToggle();
          break;
      }
    },
    [
      handleNotebookToggle,
      handleTeacherNotesToggle,
      handleTranscriptToggle,
      handleReflectionToggle,
      handleSlidesToggle,
    ],
  );

  const selectedDrawer = useMemo(() => {
    switch (view.state.subview) {
      case WatchViewSubview.Notebook:
        return 0;
      case WatchViewSubview.TeacherNotes:
        return 1;
      case WatchViewSubview.Transcript:
        return 2;
      case WatchViewSubview.Reflection:
        return 3;
      case WatchViewSubview.Downloads:
        return 4;
      default:
        return null;
    }
  }, [view.state.subview]);

  const selectedTab = useMemo(() => {
    switch (view.state.subview) {
      case WatchViewSubview.Notebook:
        return 1;
      case WatchViewSubview.TeacherNotes:
        return 2;
      case WatchViewSubview.Transcript:
        return 3;
      case WatchViewSubview.Reflection:
        return 4;
      default:
        return 0;
    }
  }, [view.state.subview]);

  const handleModalRequestSeek = useCallback(
    (timestamp: number) => {
      view.playerContextRef.current?.seek(timestamp);
      if (!view.isDesktopLayout) {
        view.setSubview(WatchViewSubview.Slides);
      }
    },
    [view],
  );

  const handleHeaderSplitscreenClick = useCallback(() => {
    if (view.state.viewMode === WatchViewViewMode.Splitscreen) return;

    view.setViewMode(WatchViewViewMode.Splitscreen);
    view.playerContextRef.current?.setViewMode?.(MediaPlayerViewMode.inline);
  }, [view]);

  const handleHeaderFreestyleClick = useCallback(() => {
    if (view.state.viewMode === WatchViewViewMode.Freestyle) return;

    view.setViewMode(WatchViewViewMode.Freestyle);
    view.playerContextRef.current?.setViewMode?.(MediaPlayerViewMode.freestyle);
  }, [view]);

  const handleHeaderFullscreenClick = useCallback(async () => {
    if (view.state.viewMode === WatchViewViewMode.Fullscreen) return;

    // Ensure PiP is fully exited if we're in freestyle mode.
    // Exiting PiP triggers a viewmode change that needs to
    // resolve prior to entering fullscreen.
    await view.exitPictureInPicture();

    view.setViewMode(WatchViewViewMode.Fullscreen);

    view.playerContextRef.current?.setViewMode?.(
      MediaPlayerViewMode.fullscreen,
    );
  }, [view]);

  const handleRequestDrawerClose = useCallback(() => {
    view.setSubview(WatchViewSubview.Slides);
  }, [view]);

  const handleActionsButtonClick = useCallback(() => {
    setIsActionsOpen(true);
  }, []);

  const handleRequestActionsClose = useCallback(() => {
    setIsActionsOpen(false);
  }, []);

  const handleRequestShareClose = useCallback(() => {
    setIsShareOpen(false);
  }, []);

  const handleRequestNoteEditorOpen = useCallback(() => {
    if (!currentUser) {
      promptLogin();
      return;
    }

    if (!view.state.draftNoteValue) {
      view.setDraftNoteTimestamp(view.state.currentTime);
    }

    view.setNoteEditorOpen(true);

    track("crNoteStart", trackingData);
  }, [currentUser, trackingData, promptLogin, view]);

  const handleNoteCancel = useCallback(() => {
    view.clearDraftNoteData();
    clearDraftData(draftNoteStorageKey);
  }, [draftNoteStorageKey, view]);

  const handleRequestNoteEditorClose = useCallback(() => {
    view.setNoteEditorOpen(false);
  }, [view]);

  const handleNoteDraftSave = useCallback(
    async (event: NoteEditorModalSaveEvent) => {
      view.setDraftNoteData(event);
      setPreference("noteColor", event.color);
      setDraftData(draftNoteStorageKey, event);
    },
    [draftNoteStorageKey, setPreference, view],
  );

  const handleNoteSave = useCallback(
    async (event: NoteEditorModalSaveEvent) => {
      view.clearDraftNoteData();

      await submitAction({
        intent: Action.CreateNote,
        input: {
          content: event.value,
          color: toApiNoteColor(event.color),
          sessionId: data.session.id,
          timestamp: view.state.draftNoteTimestamp,
        },
      })
        .then(() => {
          showToast({
            title: t("note.savedToast.success"),
            intent: "success",
            actionText: t("note.savedToast.cta"),
            onActionClick() {
              view.setSubview(WatchViewSubview.Notebook);
              dismissAllToasts();
            },
          });
        })
        .finally(() => {
          view.setNoteEditorOpen(false);
        });

      track("crNoteSave", trackingData);
    },
    [
      data.session.id,
      dismissAllToasts,
      showToast,
      submitAction,
      t,
      trackingData,
      view,
    ],
  );

  const handleRequestSeek = useCallback(
    (time: number) => {
      view.playerContextRef.current?.seek(time);
    },
    [view.playerContextRef],
  );

  const handleRequestAudioOnly = useCallback(
    async (value: boolean) => {
      // Perform some setup first
      if (value) {
        // Ensure the video player is put back in place
        // before switching to audio-only mode
        await view.exitPictureInPicture();
      } else {
        // Reset the view back to the default mode state
        view.setViewMode(WatchViewViewMode.Splitscreen);
        view.customProperties.set(
          WatchViewComputedCustomProperties.AudioPlayerHeight,
          "0px",
        );
      }

      // Set the media mode
      view.setMediaMode(
        value ? WatchViewMediaMode.Audio : WatchViewMediaMode.Video,
      );

      // Set the start time so that playback continues where left off
      view.setStartTime(view.state.currentTime);

      // Perform side-effects
      setPreference("audioOnly", value);
      track(value ? "crAudioOnlyOn" : "crAudioOnlyOff", trackingData);
    },
    [view, setPreference, trackingData],
  );

  const handleVideoRequestViewModeChange = useCallback(
    (viewMode: WatchViewViewMode) => {
      view.setViewMode(viewMode);
    },
    [view],
  );

  const handleSlidesRequestViewModeChange = useCallback(
    (viewMode: WatchViewViewMode) => {
      view.setViewMode(viewMode);
      if (view.state.mediaMode !== "video") return;

      const player = view.playerContextRef.current;

      switch (viewMode) {
        case WatchViewViewMode.Freestyle:
          // Trigger video PiP
          player?.setViewMode?.(MediaPlayerViewMode.freestyle);
          break;
        case WatchViewViewMode.Splitscreen:
          // Return to inline video mode
          player?.setViewMode?.(MediaPlayerViewMode.inline);
          break;
        default:
          break;
      }
    },
    [view],
  );

  const handleSlidesRequestSubviewChange = useCallback(
    (subview: WatchViewSubview) => {
      view.setSubview(subview);
    },
    [view],
  );

  const handleStageLayout = useCallback(
    (event: WatchViewLayoutEvent) => {
      view.handleStageLayout(event);
    },
    [view],
  );

  const handleSlidesLayout = useCallback(
    (event: WatchViewLayoutEvent) => {
      view.handleSlidesLayout(event);
    },
    [view],
  );

  const handleDrawerLayout = useCallback(
    (event: WatchViewLayoutEvent) => {
      view.handleDrawerLayout(event);
    },
    [view],
  );

  const handleAudioLayout = useCallback(
    (event: WatchViewLayoutEvent) => {
      view.handleAudioLayout(event);
    },
    [view],
  );

  const handleFullscreenWindowPositionChange = useCallback(
    (position: MediaPlayerFullscreenWindowPosition) => {
      view.setFullscreenWindowPosition(position);
    },
    [view],
  );

  const handleFullscreenSlidesChange = useCallback(
    (fullscreenSlides: boolean) => {
      view.setFullscreenSlides(fullscreenSlides);
    },
    [view],
  );

  const handleReplay = useCallback(() => {
    view.setEnded(false);
    dismissAllToasts();
  }, [dismissAllToasts, view]);

  const handleEnded = useCallback(() => {
    view.setEnded(true);

    showToast({
      id: "completed",
      actionText: t("session.completed.toast.cta"),
      autoClose: false,
      dismissable: false,
      icon: "angles-right",
      intent: "success",
      title: t("session.completed.toast.title"),
      onActionClick() {
        view.setSubview(WatchViewSubview.Completed);

        dismissAllToasts();

        track("crReviewReflect", {
          ...trackingData,
          trigger: "sessionEndedToast",
        });
      },
    });
  }, [dismissAllToasts, showToast, t, trackingData, view]);

  /////////////
  // EFFECTS //
  /////////////

  // Persist page state
  usePageState({ pageType: "session" });

  // Close the drawer when moving across breakpoints
  useEffect(() => {
    view.setSubview(WatchViewSubview.Slides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.isDesktopLayout]);

  // Reset certain view state when navigating to a new session
  useEffect(() => {
    if (!prevSessionId || prevSessionId === data.session.id) return;

    progressSaveClockRef.current.time = Date.now();
    dismissAllToasts();

    view.setState(initialState);

    if (view.state.subview === WatchViewSubview.Completed) {
      view.setSubview(WatchViewSubview.Slides);
    }

    // Track the start of a new session
    track("crSessionStart", trackingData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.session.id, prevSessionId]);

  // Keep the screen awake
  useMountEffect(() => {
    // This is re-enabled when the view is dismissed (see Link.tsx).
    sendMessageToHost({
      ui: {
        setScreenTimerDisabled: {
          disabled: true,
        },
      },
    });

    return () => {
      // This is a safeguard. The view is unloaded before
      // this can run so its unlikely to be recieved by the
      // native app. It is _actually_ re-enabled in
      // Link.tsx#cleanUp
      sendMessageToHost({
        ui: {
          setScreenTimerDisabled: {
            disabled: false,
          },
        },
      });
    };
  });

  // Ensure toasts are dismissed when switching between views.
  useUnmountEffect(() => {
    dismissAllToasts();
  });

  return (
    <WatchView
      busy={transition.state === "loading"}
      style={view.styles}
      fullscreenSlides={view.state.fullscreenSlides}
      fullscreenWindowPosition={view.state.fullscreenWindowPosition}
      mediaMode={view.state.mediaMode}
      viewMode={view.state.viewMode}
    >
      <WatchView.Header>
        <WatchView.HeaderGroup align="left">
          <Button
            {...headerButtonCommonProps}
            icon={isMobileNative(hostContext) ? "xmark" : "angle-left"}
            href={pathFor(RouteType.MODULE, {
              class: data.session.class?.slug!,
              module: String(data.session.module?.position),
            })}
            nativeBehavior="dismiss"
          />
        </WatchView.HeaderGroup>

        <WatchView.HeaderGroup align="left" full>
          <WatchView.Menu
            preLabel={t("quickNavigation.session.preTitle", {
              number: data.session.position,
            })}
            label={data.session.title}
            items={menuItems}
            currentItemId={data.session.id}
          />

          <Button
            {...headerButtonCommonProps}
            icon="ellipsis-vertical"
            onClick={handleActionsButtonClick}
          />

          <When condition={view.isDesktopLayout}>
            <ButtonGroup>
              <Button
                {...headerButtonCommonProps}
                icon="rectangle-split-2-1"
                active={
                  view.state.mediaMode === WatchViewMediaMode.Video &&
                  view.state.viewMode === WatchViewViewMode.Splitscreen
                }
                disabled={view.state.mediaMode === WatchViewMediaMode.Audio}
                onClick={handleHeaderSplitscreenClick}
              />
              <When condition={isPipSupported()}>
                <Button
                  {...headerButtonCommonProps}
                  icon="pip"
                  active={
                    view.state.mediaMode === WatchViewMediaMode.Video &&
                    view.state.viewMode === WatchViewViewMode.Freestyle
                  }
                  disabled={view.state.mediaMode === WatchViewMediaMode.Audio}
                  onClick={handleHeaderFreestyleClick}
                />
              </When>
              <Button
                {...headerButtonCommonProps}
                icon="up-right-and-down-left-from-center"
                active={view.state.viewMode === WatchViewViewMode.Fullscreen}
                disabled={view.state.mediaMode === WatchViewMediaMode.Audio}
                onClick={handleHeaderFullscreenClick}
              />
            </ButtonGroup>
          </When>
        </WatchView.HeaderGroup>

        <When condition={view.isDesktopLayout}>
          <WatchView.HeaderGroup align="right">
            <SegmentedControl
              selected={selectedDrawer}
              onSelectedChanged={handleDrawerToggle}
              options={[
                {
                  label: t("session.tab.notebook"),
                  icon: "notebook",
                },
                {
                  label: t("session.tab.teacherNotes"),
                  icon: "chalkboard-user",
                },
                {
                  label: t("session.tab.transcript"),
                  icon: "scroll",
                },
                {
                  label: t("session.tab.reflection"),
                  icon: "message-pen",
                },
                {
                  label: t("session.tab.downloads"),
                  icon: "circle-arrow-down",
                },
              ]}
            ></SegmentedControl>
            <When condition={!isMobileNative(hostContext)}>
              <SessionUserMenu />
            </When>
          </WatchView.HeaderGroup>
        </When>
      </WatchView.Header>

      <WatchView.Main
        isDrawerOpen={view.state.subview !== WatchViewSubview.Slides}
        isDesktopLayout={view.isDesktopLayout}
      >
        <WatchView.Stage
          viewMode={view.state.viewMode}
          mediaMode={view.state.mediaMode}
          onLayout={handleStageLayout}
        >
          {view.state.mediaMode === WatchViewMediaMode.Video && (
            <WatchView.StagePane>
              <WatchView.Video>
                <SessionVideoPlayer
                  playerContextRef={view.playerContextRef}
                  // FIXME: State management within the Video player is a bit of a mess. Some props come from the loader data, some come from the watch view manager. The latter runs client-side, so its one render cycle behind the loader data. In the future we should refactor how state within the Video player is managed in order to avoid this issue
                  startTime={data.startTime}
                  onEnded={handleEnded}
                  onFullscreenSlidesChange={handleFullscreenSlidesChange}
                  onFullscreenWindowPositionChange={
                    handleFullscreenWindowPositionChange
                  }
                  onReplay={handleReplay}
                  onRequestViewModeChange={handleVideoRequestViewModeChange}
                  onTimeUpdate={handleTimeUpdate}
                />
              </WatchView.Video>
            </WatchView.StagePane>
          )}

          {view.state.mediaMode === WatchViewMediaMode.Audio && (
            <WatchView.Audio onLayout={handleAudioLayout}>
              <SessionAudioPlayer
                isDesktopLayout={view.isDesktopLayout}
                startTime={data.startTime}
                playerContextRef={view.playerContextRef}
                onEnded={handleEnded}
                onReplay={handleReplay}
                onTimeUpdate={handleTimeUpdate}
              />
            </WatchView.Audio>
          )}
          <WatchView.StagePane>
            <When
              condition={
                view.isDesktopLayout ||
                view.state.viewMode === WatchViewViewMode.Fullscreen
              }
            >
              <WatchView.Slides onLayout={handleSlidesLayout}>
                <SessionSlides
                  canExpand={canExpandSlides}
                  currentTime={view.state.currentTime}
                  disableVideo={Boolean(
                    view.playerContextRef.current?.airplaying,
                  )}
                  fullscreen={
                    view.state.viewMode === WatchViewViewMode.Fullscreen
                  }
                  paused={Boolean(view.playerContextRef.current?.paused)}
                  viewMode={view.state.viewMode}
                  onRequestSeek={handleRequestSeek}
                  onRequestSubviewChange={handleSlidesRequestSubviewChange}
                  onRequestViewModeChange={handleSlidesRequestViewModeChange}
                  onSaveSlideToNotes={handleSaveSlideToNotes}
                />
              </WatchView.Slides>

              <WatchView.SlideLinks>
                <SessionSlideLinks
                  currentTime={view.state.currentTime}
                  onRequestSubviewChange={handleSlidesRequestSubviewChange}
                />
              </WatchView.SlideLinks>
            </When>
            <When
              condition={
                !view.isDesktopLayout &&
                view.state.viewMode !== WatchViewViewMode.Fullscreen
              }
            >
              <Tabs
                className="SessionDetail__tabs"
                selected={selectedTab}
                onSelectedChanged={handleTabsToggle}
              >
                <TabList className="SessionDetail__tab-list">
                  <Tab>{t("session.tab.slides")}</Tab>
                  <Tab>{t("session.tab.notebook")}</Tab>
                  <Tab>{t("session.tab.teacherNotes")}</Tab>
                  <Tab>{t("session.tab.transcript")}</Tab>
                  <Tab>{t("session.tab.reflection")}</Tab>
                </TabList>

                <TabPanel className="SessionDetail__tab-panel">
                  <WatchView.Slides onLayout={handleSlidesLayout}>
                    <SessionSlides
                      canExpand={canExpandSlides}
                      currentTime={view.state.currentTime}
                      disableVideo={Boolean(
                        view.playerContextRef.current?.airplaying,
                      )}
                      fullscreen={
                        view.state.viewMode === WatchViewViewMode.Fullscreen
                      }
                      paused={Boolean(view.playerContextRef.current?.paused)}
                      viewMode={view.state.viewMode}
                      onRequestSeek={handleRequestSeek}
                      onRequestSubviewChange={handleSlidesRequestSubviewChange}
                      onRequestViewModeChange={
                        handleSlidesRequestViewModeChange
                      }
                      onSaveSlideToNotes={handleSaveSlideToNotes}
                    />
                  </WatchView.Slides>
                  <WatchView.SlideLinks>
                    <SessionSlideLinks
                      currentTime={view.state.currentTime}
                      onRequestSubviewChange={handleSlidesRequestSubviewChange}
                    />
                  </WatchView.SlideLinks>
                </TabPanel>
                <TabPanel className="SessionDetail__tab-panel">
                  <SessionNotebook
                    onRequestSeek={handleRequestSeek}
                    isDesktopLayout={view.isDesktopLayout}
                  />
                </TabPanel>
                <TabPanel className="SessionDetail__tab-panel">
                  <SessionTeacherNotes isDesktopLayout={view.isDesktopLayout} />
                </TabPanel>
                <TabPanel className="SessionDetail__tab-panel">
                  <SessionTranscript
                    currentTime={view.state.currentTime}
                    onRequestSeek={handleModalRequestSeek}
                    isDesktopLayout={view.isDesktopLayout}
                  />
                </TabPanel>
                <TabPanel className="SessionDetail__tab-panel">
                  <SessionReflection
                    onReflectionSave={handleReflectionSave}
                    isDesktopLayout={view.isDesktopLayout}
                  />
                </TabPanel>
              </Tabs>
            </When>
          </WatchView.StagePane>
        </WatchView.Stage>

        <WatchView.Drawer
          isOpen={
            view.isDesktopLayout
              ? view.state.subview !== WatchViewSubview.Slides
              : view.state.subview === WatchViewSubview.Completed
          }
          isPrimary={view.state.subview === WatchViewSubview.Completed}
          onLayout={handleDrawerLayout}
          onRequestClose={handleRequestDrawerClose}
          isDesktopLayout={view.isDesktopLayout}
        >
          {view.state.subview === WatchViewSubview.Notebook && (
            <SessionNotebook
              onRequestSeek={handleModalRequestSeek}
              onRequestClose={handleRequestDrawerClose}
              isDesktopLayout={view.isDesktopLayout}
            />
          )}

          {view.state.subview === WatchViewSubview.TeacherNotes && (
            <SessionTeacherNotes
              onRequestClose={handleRequestDrawerClose}
              isDesktopLayout={view.isDesktopLayout}
            />
          )}

          {view.state.subview === WatchViewSubview.Transcript && (
            <SessionTranscript
              currentTime={view.state.currentTime}
              onRequestSeek={handleModalRequestSeek}
              onRequestClose={handleRequestDrawerClose}
              isDesktopLayout={view.isDesktopLayout}
            />
          )}

          {view.state.subview === WatchViewSubview.Reflection && (
            <SessionReflection
              onRequestClose={handleRequestDrawerClose}
              onReflectionSave={handleReflectionSave}
              isDesktopLayout={view.isDesktopLayout}
            />
          )}

          {view.state.subview === WatchViewSubview.Downloads && (
            <SessionDownloads onRequestClose={handleRequestDrawerClose} />
          )}

          {view.state.subview === WatchViewSubview.Completed && (
            <SessionReviewReflect
              onRequestClose={handleRequestDrawerClose}
              isDesktopLayout={view.isDesktopLayout}
            />
          )}
        </WatchView.Drawer>
      </WatchView.Main>

      <WatchView.Dock>
        <When condition={view.isDesktopLayout}>
          <WatchView.DockedNote
            color={view.state.draftNoteColor}
            placeholder={t("textEditor.placeholder.note")}
            value={view.state.draftNoteValue ?? ""}
            onClick={handleRequestNoteEditorOpen}
          />
        </When>

        <When condition={!view.isDesktopLayout}>
          <WatchView.DockedControls>
            <TextInput
              readOnly
              placeholder={t("textEditor.placeholder.note")}
              value={view.state.draftNoteValue ?? ""}
              onClick={handleRequestNoteEditorOpen}
            />
          </WatchView.DockedControls>
        </When>
      </WatchView.Dock>

      <WatchView.NoteEditor
        color={view.state.draftNoteColor}
        id={data.session.id}
        initialValue={view.state.draftNoteValue}
        isOpen={view.state.isNoteEditorOpen}
        timestamp={view.state.draftNoteTimestamp}
        title={t("textEditor.heading.newNote")}
        onCancel={handleNoteCancel}
        onDraftSave={handleNoteDraftSave}
        onRequestClose={handleRequestNoteEditorClose}
        onSave={handleNoteSave}
      />

      {view.state.viewMode !== WatchViewViewMode.Fullscreen && (
        <>
          <SessionActionsModal
            actions={actions}
            artwork={
              getArtworkFromData(
                data.session.module?.artwork as ClassroomImage[],
                "1:1",
              )?.url
            }
            audioOnlyToggleDisabled={view.playerContextRef.current?.airplaying}
            isAudioOnly={view.state.mediaMode === WatchViewMediaMode.Audio}
            isOpen={isActionsOpen}
            postTitle={t("session.modal.actions.postTitle", {
              number: data.session.position,
            })}
            title={data.session.title}
            onRequestAudioOnly={handleRequestAudioOnly}
            onRequestClose={handleRequestActionsClose}
          />
          <ShareModal
            shareUrl={shareUrl}
            isOpen={isShareOpen}
            onRequestClose={handleRequestShareClose}
          />
        </>
      )}

      <ToastStack position="end" insetBottom={toastStackInsetBottom} />
    </WatchView>
  );
}
